/**
 * CortexSentinel — MS-19
 *
 * Dashboard agregado das defesas Cortex que rodam silenciosamente no pipeline:
 *  - Banter detections (downgrades de intent factual_critical → casual)
 *  - Admin directives (parse de comandos por admins de grupo)
 *  - Topic blacklist (tópicos bloqueados permanentemente por admin)
 *  - Red flags (suprime referência ao tópico em janela de 24h)
 *
 * Frontend puro, leitura via Supabase client. Sem migration.
 */
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, MessageSquareWarning, ShieldOff, Megaphone } from "lucide-react";

type Counts = {
  banter_24h: number;
  banter_7d: number;
  directives_24h: number;
  directives_7d: number;
  blacklist_total: number;
  redflags_active: number;
};

type Banter = {
  id: string;
  group_id: string;
  banter_score: number;
  downgraded_intent_from: string | null;
  downgraded_intent_to: string | null;
  signals: Record<string, unknown> | null;
  created_at: string;
};

type Directive = {
  id: string;
  group_id: string;
  raw_text: string;
  parsed_action: string | null;
  parsed_target: string | null;
  applied_action: string | null;
  confidence: number | null;
  source: string | null;
  created_at: string;
};

type Blacklist = {
  id: string;
  group_id: string;
  topic_label: string;
  reason: string | null;
  active: boolean;
  created_at: string;
};

type RedFlag = {
  id: string;
  group_id: string;
  topic_label: string;
  expires_at: string | null;
  created_at: string;
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

function shortId(id: string | null | undefined): string {
  return id ? id.slice(0, 8) : "—";
}

export default function CortexSentinel() {
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState<Counts | null>(null);
  const [banter, setBanter] = useState<Banter[]>([]);
  const [directives, setDirectives] = useState<Directive[]>([]);
  const [blacklist, setBlacklist] = useState<Blacklist[]>([]);
  const [redflags, setRedflags] = useState<RedFlag[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const since24h = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
        const since7d = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

        const [
          banter24h,
          banter7d,
          dir24h,
          dir7d,
          blackTotal,
          redActive,
          banterRows,
          dirRows,
          blackRows,
          redRows,
        ] = await Promise.all([
          supabase.from("banter_detections").select("id", { count: "exact", head: true }).gte("created_at", since24h),
          supabase.from("banter_detections").select("id", { count: "exact", head: true }).gte("created_at", since7d),
          supabase.from("admin_directives_log").select("id", { count: "exact", head: true }).gte("created_at", since24h),
          supabase.from("admin_directives_log").select("id", { count: "exact", head: true }).gte("created_at", since7d),
          supabase.from("group_topic_blacklist").select("id", { count: "exact", head: true }).eq("active", true),
          supabase.from("working_memory_redflags").select("id", { count: "exact", head: true }).or(`expires_at.is.null,expires_at.gt.${new Date().toISOString()}`),
          supabase.from("banter_detections").select("id, group_id, banter_score, downgraded_intent_from, downgraded_intent_to, signals, created_at").order("created_at", { ascending: false }).limit(50),
          supabase.from("admin_directives_log").select("id, group_id, raw_text, parsed_action, parsed_target, applied_action, confidence, source, created_at").order("created_at", { ascending: false }).limit(50),
          supabase.from("group_topic_blacklist").select("id, group_id, topic_label, reason, active, created_at").order("created_at", { ascending: false }).limit(50),
          supabase.from("working_memory_redflags").select("id, group_id, topic_label, expires_at, created_at").order("created_at", { ascending: false }).limit(50),
        ]);

        if (cancelled) return;

        setCounts({
          banter_24h: banter24h.count ?? 0,
          banter_7d: banter7d.count ?? 0,
          directives_24h: dir24h.count ?? 0,
          directives_7d: dir7d.count ?? 0,
          blacklist_total: blackTotal.count ?? 0,
          redflags_active: redActive.count ?? 0,
        });
        setBanter((banterRows.data ?? []) as Banter[]);
        setDirectives((dirRows.data ?? []) as Directive[]);
        setBlacklist((blackRows.data ?? []) as Blacklist[]);
        setRedflags((redRows.data ?? []) as RedFlag[]);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Cortex Sentinel</h2>
        <p className="text-sm text-muted-foreground">
          Defesas que rodam silenciosamente no pipeline: detecção de banter (downgrade de intent), parse de diretivas de admin, tópicos bloqueados e red flags de janela curta.
        </p>
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6 text-sm text-destructive">Falha ao carregar: {error}</CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={<MessageSquareWarning className="h-4 w-4" />}
          label="Banter (24h / 7d)"
          value={loading ? null : `${counts?.banter_24h ?? 0} / ${counts?.banter_7d ?? 0}`}
          hint="Downgrades factual → casual"
        />
        <KpiCard
          icon={<Megaphone className="h-4 w-4" />}
          label="Diretivas admin (24h / 7d)"
          value={loading ? null : `${counts?.directives_24h ?? 0} / ${counts?.directives_7d ?? 0}`}
          hint="Comandos de admin parseados"
        />
        <KpiCard
          icon={<ShieldOff className="h-4 w-4" />}
          label="Tópicos bloqueados"
          value={loading ? null : String(counts?.blacklist_total ?? 0)}
          hint="Permanentes por admin"
        />
        <KpiCard
          icon={<AlertTriangle className="h-4 w-4" />}
          label="Red flags ativas"
          value={loading ? null : String(counts?.redflags_active ?? 0)}
          hint="Janela curta (≤24h)"
        />
      </div>

      <Tabs defaultValue="banter">
        <TabsList>
          <TabsTrigger value="banter">Banter</TabsTrigger>
          <TabsTrigger value="directives">Diretivas</TabsTrigger>
          <TabsTrigger value="blacklist">Blacklist</TabsTrigger>
          <TabsTrigger value="redflags">Red Flags</TabsTrigger>
        </TabsList>

        <TabsContent value="banter" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Últimas detecções de banter</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-32 w-full" />
              ) : banter.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sem detecções recentes.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Quando</TableHead>
                      <TableHead>Grupo</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead>Downgrade</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {banter.map((b) => (
                      <TableRow key={b.id}>
                        <TableCell className="text-xs">{formatDate(b.created_at)}</TableCell>
                        <TableCell className="text-xs font-mono">{shortId(b.group_id)}</TableCell>
                        <TableCell>
                          <Badge variant={b.banter_score >= 0.65 ? "default" : "secondary"}>
                            {Number(b.banter_score).toFixed(2)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">
                          {b.downgraded_intent_from && b.downgraded_intent_to
                            ? `${b.downgraded_intent_from} → ${b.downgraded_intent_to}`
                            : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="directives" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Últimas diretivas de admin</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-32 w-full" />
              ) : directives.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sem diretivas recentes.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Quando</TableHead>
                      <TableHead>Grupo</TableHead>
                      <TableHead>Texto</TableHead>
                      <TableHead>Ação</TableHead>
                      <TableHead>Aplicada</TableHead>
                      <TableHead>Conf.</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {directives.map((d) => (
                      <TableRow key={d.id}>
                        <TableCell className="text-xs whitespace-nowrap">{formatDate(d.created_at)}</TableCell>
                        <TableCell className="text-xs font-mono">{shortId(d.group_id)}</TableCell>
                        <TableCell className="text-xs max-w-xs truncate" title={d.raw_text}>
                          {d.raw_text}
                        </TableCell>
                        <TableCell className="text-xs">
                          {d.parsed_action ? (
                            <Badge variant="outline">
                              {d.parsed_action}
                              {d.parsed_target ? ` · ${d.parsed_target}` : ""}
                            </Badge>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          {d.applied_action ? (
                            <Badge variant="default">{d.applied_action}</Badge>
                          ) : (
                            <Badge variant="secondary">none</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          {d.confidence != null ? Number(d.confidence).toFixed(2) : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="blacklist" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Tópicos bloqueados (permanente)</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-32 w-full" />
              ) : blacklist.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhum tópico bloqueado.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Quando</TableHead>
                      <TableHead>Grupo</TableHead>
                      <TableHead>Tópico</TableHead>
                      <TableHead>Motivo</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {blacklist.map((b) => (
                      <TableRow key={b.id}>
                        <TableCell className="text-xs whitespace-nowrap">{formatDate(b.created_at)}</TableCell>
                        <TableCell className="text-xs font-mono">{shortId(b.group_id)}</TableCell>
                        <TableCell className="text-xs">{b.topic_label}</TableCell>
                        <TableCell className="text-xs max-w-xs truncate" title={b.reason ?? ""}>
                          {b.reason ?? "—"}
                        </TableCell>
                        <TableCell>
                          <Badge variant={b.active ? "default" : "secondary"}>
                            {b.active ? "ativo" : "inativo"}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="redflags" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Red flags (janela curta)</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-32 w-full" />
              ) : redflags.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sem red flags ativas.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Criada em</TableHead>
                      <TableHead>Grupo</TableHead>
                      <TableHead>Tópico</TableHead>
                      <TableHead>Expira</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {redflags.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="text-xs whitespace-nowrap">{formatDate(r.created_at)}</TableCell>
                        <TableCell className="text-xs font-mono">{shortId(r.group_id)}</TableCell>
                        <TableCell className="text-xs">{r.topic_label}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">
                          {r.expires_at ? formatDate(r.expires_at) : "sem expiração"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function KpiCard({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | null;
  hint: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
          {icon}
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold tabular-nums">
          {value ?? <Skeleton className="h-7 w-16" />}
        </div>
        <p className="text-xs text-muted-foreground mt-1">{hint}</p>
      </CardContent>
    </Card>
  );
}
